/**
 * Data hook for the read-only attachment list on the requirement detail page.
 * GET /api/requirements/{id}/attachments/ — any authenticated user.
 */
import { useCallback, useEffect, useState } from 'react';

import { ApiError, requirements } from '@/helpers/ApiHelper';
import type { IntakeAttachment } from '@/types/intake';

export interface UseRequirementAttachmentsResult {
  data: IntakeAttachment[];
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
}

export function useRequirementAttachments(id: string): UseRequirementAttachmentsResult {
  const [data, setData] = useState<IntakeAttachment[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [token, setToken] = useState(0);

  const refetch = useCallback(() => setToken((t) => t + 1), []);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setError(null);
    requirements
      .listAttachments(id)
      .then((r) => {
        if (!cancelled) setData(r.results);
      })
      .catch((e: unknown) => {
        if (!cancelled) setError(e instanceof ApiError ? e.detail : 'Failed to load attachments');
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id, token]);

  return { data, isLoading, error, refetch };
}
